import type { HTTPValidationError } from '$lib/api/generated';
import * as m from '$lib/paraglide/messages';

export type ApiRequestErrorCode =
	| 'CATEGORY_REQUEST_FAILED'
	| 'TRACKER_INVALID_INPUT'
	| 'TRACKER_REQUEST_FAILED'
	| 'FAVORITE_REQUEST_FAILED'
	| 'METRIC_SCHEMA_CONFLICT_HAS_HISTORY'
	| 'HEALTH_CONSENT_REQUIRED';

export class ApiRequestError extends Error {
	readonly code: ApiRequestErrorCode;

	constructor(code: ApiRequestErrorCode, displayMessage: string = m.common_unexpected_error()) {
		super(displayMessage);
		this.name = 'ApiRequestError';
		this.code = code;
	}
}

export function createApiRequestError(
	code: ApiRequestErrorCode,
	displayMessage?: string
): ApiRequestError {
	return new ApiRequestError(code, displayMessage);
}

/**
 * samlla#v757: `customInstance` (api/orval/mutator.ts) throws a plain
 * `{ code, status }` object on a non-OK response, not an `Error`. Use this to
 * detect one specific stable error code before falling back to a generic
 * `ApiRequestError`.
 */
export function isApiFailureCode(error: unknown, code: string): boolean {
	return (
		typeof error === 'object' &&
		error !== null &&
		'code' in error &&
		(error as { code?: unknown }).code === code
	);
}

/**
 * Type guard to check if an error is a FastAPI HTTPValidationError
 */
export function isValidationError(error: unknown): error is HTTPValidationError {
	if (typeof error !== 'object' || error === null) return false;

	const err = error as Record<string, unknown>;
	return (
		'detail' in err &&
		Array.isArray(err.detail) &&
		err.detail.length > 0 &&
		typeof err.detail[0] === 'object' &&
		err.detail[0] !== null &&
		'msg' in (err.detail[0] as Record<string, unknown>)
	);
}

/**
 * Extract error message from a potential validation error
 */
export function getErrorMessage(
	error: unknown,
	fallback: string = m.common_unexpected_error()
): string {
	if (isValidationError(error)) {
		return error.detail?.[0]?.msg || fallback;
	}

	if (error instanceof Error) {
		return error.message;
	}

	if (typeof error === 'string') {
		return error;
	}

	return fallback;
}
